import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi, customerAuth as customerAuthApi } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('admin_user');
    const adminToken = localStorage.getItem('admin_token');
    const savedCustomer = localStorage.getItem('customer_user');
    const customerToken = localStorage.getItem('customer_token');

    if (adminToken && savedUser) {
      setUser(JSON.parse(savedUser));
      authApi.me().then((u) => {
        setUser(u.user || u);
        localStorage.setItem('admin_user', JSON.stringify(u.user || u));
      }).catch(() => {
        setUser(null);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      }).finally(() => setLoading(false));
    } else if (customerToken && savedCustomer) {
      setCustomer(JSON.parse(savedCustomer));
      customerAuthApi.me().then((res) => {
        const c = res.item || res.customer || res;
        setCustomer(c);
        localStorage.setItem('customer_user', JSON.stringify(c));
      }).catch(() => {
        setCustomer(null);
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_user');
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === 'success' && params.get('type') === 'customer') {
      const token = params.get('t');
      if (token) {
        localStorage.setItem('customer_token', token);
      }
      customerAuthApi.me().then((res) => {
        const c = res.item || res.customer || res;
        localStorage.setItem('customer_user', JSON.stringify(c));
        setCustomer(c);
      }).catch(() => {
        console.error('Google OAuth: failed to fetch customer');
      }).finally(() => {
        window.history.replaceState({}, '', window.location.pathname);
      });
    } else if (params.get('auth') === 'success') {
      const token = params.get('t');
      if (token) {
        localStorage.setItem('admin_token', token);
        authApi.me().then((u) => {
          setUser(u.user || u);
          localStorage.setItem('admin_user', JSON.stringify(u.user || u));
        }).catch(() => {});
      } else {
        authApi.me().then((u) => {
          setUser(u.user || u);
          localStorage.setItem('admin_user', JSON.stringify(u.user || u));
        }).catch(() => {});
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('auth') === 'error') {
      console.error('Google OAuth error:', params.get('reason'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const login = useCallback(async (email, password, turnstileToken) => {
    const data = await authApi.login(email, password, turnstileToken);
    const token = data.tokens?.accessToken || data.accessToken;
    const userData = data.user || data;

    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const loginCustomer = useCallback(async (email, password, turnstileToken) => {
    const data = await customerAuthApi.login(email, password, turnstileToken);
    const token = data.tokens?.accessToken || data.accessToken;
    const customerData = data.item || data.customer || data;

    localStorage.setItem('customer_token', token);
    localStorage.setItem('customer_user', JSON.stringify(customerData));
    setCustomer(customerData);
    return customerData;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
  }, []);

  const logoutCustomer = useCallback(async () => {
    try { await customerAuthApi.logout(); } catch {}
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    setCustomer(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      customer,
      loading,
      login,
      loginCustomer,
      logout,
      logoutCustomer,
      isAuthenticated: !!user || !!customer,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthContext');
  return ctx;
}
